import * as React from 'react';
import Box from '../../../parts/Box';
import { Button } from 'reactstrap';
import {ll_url} from '../../../utils/tools';
import {NavLink as RouterNavLink} from "react-router-dom";
import { basePath, toolName } from "../../../settings";
import KnownTargetComponent from "./KnownTargetComponent";
import { ShowOnce } from "@loschmidt/commons";
import { LicenseTypeEnum, License } from '../types/license';
import * as Messages from "../../../Messages";

import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import RunJobButton from '../components/RunJobButton';
import { useCallback, useRef } from "react";
import { useInputContext } from "../context/input-context";

library.add(faExclamationTriangle);

function InputComponent() {

  // Context:

  const {
    emailField, setEmailField,
    targetSelected,
    vinaJobDesiredRun, setVinaJobDesiredRun,
    licenseForm, setLicenseForm,
    mutations,
  } = useInputContext();

  const knownTargetSelected = Boolean(targetSelected);
  const mutationSelected = Boolean(mutations && mutations.length > 0);

  // Refs:

  const infoRef = useRef<HTMLFormElement>(null);

  // Callbacks:

  const getValid = useCallback((): boolean => {
    let valid = true;

    if (!knownTargetSelected) {
      alert("No target protein was selected.");
      valid = false;
    } else if (!mutationSelected) {
      alert("No mutation was selected.");
      valid = false;
    }

    valid = valid && infoRef.current !== null && infoRef.current.reportValidity();
    return valid;
  }, [knownTargetSelected, mutationSelected]);

  const handleLicenseRadioChange = useCallback(({ target }) => {
    setLicenseForm(licenseForm => ({ ...licenseForm, license: Number(target.value) }))
  }, [setLicenseForm]);

  const handleCommercialChange = useCallback(({ target }) => {
    setLicenseForm(licenseForm => ({ ...licenseForm, commercial: { ...licenseForm.commercial, [target.value]: target.checked } }));
  }, [setLicenseForm]);


  function renderLicenseRadioInput(type: LicenseTypeEnum) {
    return <input
      type="radio"
      value={type}
      checked={licenseForm.license === type}
      onChange={handleLicenseRadioChange}
    />
  }
  function renderCommercialCheckbox(tool: keyof License['commercial']) {
    return <input
      type="checkbox"
      disabled={licenseForm.license !== LicenseTypeEnum.COMMERCIAL}
      value={tool}
      checked={licenseForm.commercial[tool]}
      onChange={handleCommercialChange}
    />
  }

  const emailRequired = false;

  const nextDisabled = (!knownTargetSelected || !mutationSelected);

  // check whether mutated residue can be used for Vina job
  const residuesAvailableForVinaJob = targetSelected?.residuesAvailableForVinaJob;
  const noVinaJobAllowedForMutatedResidue: boolean = residuesAvailableForVinaJob ? (
    mutations.some(m => !residuesAvailableForVinaJob.includes(m.position))
  ) : false;

  return <div>
    <ShowOnce basePath={basePath} name="aboutBoxHidden" renderContent={renderAbout} permanent={true} />

    <KnownTargetComponent />

    {mutationSelected && <>

      <Box title="Virtual screening of inhibitors">
        <label style={{ margin: '0.2rem 0 0.3rem' }}>
          <input
            type="checkbox"
            checked={vinaJobDesiredRun}
            disabled={noVinaJobAllowedForMutatedResidue}
            onChange={evt => setVinaJobDesiredRun(evt.target.checked)}
          />
          <span style={{ marginLeft: '0.3rem' }}>Enable virtual screening of inhibitors</span>
        </label>
        {noVinaJobAllowedForMutatedResidue ? (
          Messages.VIRTUAL_SCREENING_UNAVAILABLE()
        ) : vinaJobDesiredRun ? (
          Messages.VIRTUAL_SCREENING_INPUT_PAGE()
        ) : (
          Messages.VIRTUAL_SCREENING_INFO_LATER()
        )}
      </Box>

      <Box title="Job information">
        <form id="ll-input-info" ref={infoRef}>
          <div className="form-group">
            <label htmlFor="inputEmail">
              Email
              {!emailRequired && ' (strongly recommended)'}
            </label>
            <input type="email" className="form-control" id="inputEmail"
                   value={emailField}
                   onChange={evt => setEmailField(evt.target.value)}
                   required={emailRequired}
                   maxLength={50}  /* email is varchar(50) in SQL */
            />
          </div>
          <div className="form-group">
            <p style={{ marginBottom: '0.7rem' }}>License agreement</p>
            <div className="ll-license-agreement">
              <div><label>
                {renderLicenseRadioInput(LicenseTypeEnum.ACADEMIC)}
                <span>I agree with the academic license agreement and confirm that I will use the software exclusively for non-commercial purposes.</span>
              </label></div>
              <div><label>
                {renderLicenseRadioInput(LicenseTypeEnum.COMMERCIAL)}
                <span>I am a commercial user and I own commercial licenses for the following:</span>
              </label></div>
              <div className="ll-nested">
                <div><label>
                  {renderCommercialCheckbox('rosetta')}
                  <span>Rosetta</span>
                </label></div>
                <div><label>
                  {renderCommercialCheckbox('foldx')}
                  <span>FoldX</span>
                </label></div>
              </div>
              <div><label>
                {renderLicenseRadioInput(LicenseTypeEnum.NO_LICENSE)}
                <span>I am a commercial user and I do not own commercial licenses for Rosetta and FoldX.</span>
              </label></div>
            </div>
            {((licenseForm.license === LicenseTypeEnum.COMMERCIAL && (
              !licenseForm.commercial.rosetta ||
              !licenseForm.commercial.foldx
            )) || licenseForm.license === LicenseTypeEnum.NO_LICENSE) && (
              <div style={{ marginTop: '0.7rem' }}>
                {Messages.LICENSE_MISSING()}
              </div>
            )}
          </div>
        </form>
        <p><em>{toolName} is free and open to all users and there is no login requirement.</em></p>
      </Box>

      <div style={{ textAlign: 'center' }}>
      <RunJobButton
        disabled={nextDisabled}
        beforeSubmit={getValid}
        buttonProps={{
          title: nextDisabled ? 'Please select target protein and mutation' : null,
          className: "ll-not-allowed-cursor-on-disabled"
        }}
      >
        Run job
      </RunJobButton>
    </div>

    </>}

  </div>

}

const renderAbout = (hide: () => void) => (
  <Box title="About">
    <p className="ll-about-text">{Messages.OPENING_DESCRIPTION()}</p>
    <p className="ll-about-links"><span><RouterNavLink to={ll_url('/help')}>User guide</RouterNavLink> | <RouterNavLink to={ll_url('/job/example')}>Example results</RouterNavLink></span><Button color="secondary" title="Forever" onClick={hide}>Hide</Button></p>
  </Box>
)

export default InputComponent;
